import { useState, useCallback } from 'react';
import axios from 'axios';

// Add base API URL for production
const API_BASE_URL = 'https://api.leadtrek.ai';

// Determine whether we're in production
const isProduction = process.env.NODE_ENV === 'production';

function useRequest() {
  const [state, setState] = useState({
    data: null,
    loading: false,
    error: null,
    statusCode: null
  });

  const getFullUrl = (endpoint) => {
    // In production, use the full API URL
    if (isProduction) {
      // Make sure endpoint starts with /
      const formattedEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
      return `${API_BASE_URL}${formattedEndpoint}`;
    }
    // In development, use relative URLs (proxy will handle it)
    return endpoint;
  };

  const refreshToken = useCallback(async () => {
    try {
      const refreshToken = localStorage.getItem('refreshToken');
      if (!refreshToken) {
        throw new Error('No refresh token available');
      }

      const response = await axios.post(getFullUrl('/customer/token/refresh/'), {
        refresh: refreshToken
      });

      localStorage.setItem('token', response.data.access);
      return response.data.access;
    } catch (error) {
      // Clear tokens and redirect to login if refresh fails
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('userData');
      window.location.href = '/login';
      throw error;
    }
  }, []);

  const sendRequest = useCallback(async (method, endpoint, data, headers = {}) => {
    try {
      setState(prev => ({ 
        ...prev, 
        loading: true, 
        error: null, 
        statusCode: null 
      }));

      let token = localStorage.getItem('token');
      let retryCount = 0;
      const maxRetries = 1;

      while (retryCount <= maxRetries) {
        try {
          // Convert data to FormData if it's an object
          let formData = new FormData();
          if (data && typeof data === 'object') {
            Object.keys(data).forEach(key => {
              formData.append(key, data[key]);
            });
          }

          const config = {
            method,
            url: getFullUrl(endpoint),
            data: formData,
            headers: {
              ...(token && { Authorization: `Bearer ${token}` }),
              ...headers,
            },
          };

          const response = await axios(config);

          setState(prev => ({
            ...prev,
            data: response.data,
            loading: false,
            statusCode: response.status
          }));

          return response.data;
        } catch (error) {
          if (error.response?.status === 401 && retryCount < maxRetries) {
            // Token expired, try to refresh
            token = await refreshToken();
            retryCount++;
            continue;
          }
          throw error;
        }
      }
    } catch (error) {
      const errorResponse = {
        statusCode: error.response?.status,
        message: error.response?.data?.detail || error.message,
        data: error.response?.data
      };

      setState(prev => ({
        ...prev,
        loading: false,
        error: errorResponse.message,
        statusCode: errorResponse.statusCode,
        data: errorResponse.data
      }));
      
      throw errorResponse;
    }
  }, [refreshToken]);

  return {
    ...state,
    sendRequest,
  };
}

export default useRequest; 